// web/src/copilot/actions/switchMaterialAction.tsx
//
// Slice 3 / Task F2: useCopilotAction("switch_material") wraps the
// DashboardController's pane setters so the chat agent can swap which
// material is shown in the left or right pane.
//
// If the user is not on /dashboard when the agent invokes the action,
// the handler navigates there first — the pane change is otherwise
// invisible. Returns `{switched, side, material_id}` so the agent can
// confirm the side-effect.
//
// Spec §5.4 — UI action wrapper.
import { useCopilotAction } from "@copilotkit/react-core";
import { useNavigate, useLocation } from "react-router-dom";

import { useDashboardController } from "../../trading/DashboardController";

type Side = "left" | "right";

type SwitchMaterialArgs = {
  side: Side;
  material_id: string;
};

export function useSwitchMaterialAction(): void {
  const { setLeftMaterialId, setRightMaterialId } = useDashboardController();
  const navigate = useNavigate();
  const location = useLocation();

  useCopilotAction({
    name: "switch_material",
    description:
      "Switch which material is shown in the dashboard's left or right pane. " +
      "Navigates to /dashboard first if the user isn't already there. " +
      "Use this when the user asks to view, focus, or compare a different " +
      'instrument (e.g. "switch left pane to GBPUSD").',
    parameters: [
      {
        name: "side",
        type: "string",
        enum: ["left", "right"],
        required: true,
      },
      { name: "material_id", type: "string", required: true },
    ],
    handler: async ({ side, material_id }: SwitchMaterialArgs) => {
      if (!location.pathname.startsWith("/dashboard")) {
        navigate("/dashboard");
      }
      if (side === "left") {
        setLeftMaterialId(material_id);
      } else {
        setRightMaterialId(material_id);
      }
      return { switched: true, side, material_id };
    },
  });
}
